import { useMemo } from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { ProductPositionInstallationReferenceTypename } from "shared/types/InstallationReference"
import { ProductPositionInstallationReference } from "shared/types/__generated__/graphql"

import { InstallationReferenceFilterState } from "entities/building-references/types/InstallationReferenceFilterState"
import { getProductType } from "entities/products/lib/getProductType"
import { useProductObjCache } from "entities/products/model/store/useProductObjCache"
import { useInstallationReferenceSelectionModel } from "widgets/installation-reference-selection/model/useInstallationReferenceSelectionModel"

import { InstallationReferenceSelectionOverlayConnectorProps } from "../InstallationReferenceSelectionOverlayConnector"

export type InstallationReferenceSelectionOverlayModel = ReturnType<
  typeof useInstallationReferenceSelectionOverlayModel
>

export function useInstallationReferenceSelectionOverlayModel({
  payload: { productPosition, onSubmit }
}: InstallationReferenceSelectionOverlayConnectorProps) {
  const { closeOverlay } = useCurrentOverlay()
  const { getCacheForVariantKey } = useProductObjCache()

  const defaultFilters: InstallationReferenceFilterState = useMemo(() => {
    const cachedObj = productPosition?.variantKey
      ? getCacheForVariantKey(productPosition.variantKey)
      : null
    return {
      products: cachedObj ? [getProductType(cachedObj.variantKey)] : []
    }
  }, [productPosition, getCacheForVariantKey])

  const defaultSelectedInstallationReferenceIDs = useMemo(
    () => productPosition?.references?.map(reference => reference.permalink),
    [productPosition?.references]
  )

  const handleSubmit = useEvent((installationReferenceIDs: string[] | null) => {
    const filteredReferences: ProductPositionInstallationReference[] =
      productPosition?.references
        ?.filter(reference =>
          installationReferenceIDs?.some(id => reference.permalink === id)
        )
        .map((reference, index) => ({
          ...reference,
          position: index
        })) || []
    const referencesToBeCreated: ProductPositionInstallationReference[] =
      installationReferenceIDs
        ?.filter(
          id =>
            !productPosition?.references?.some(
              reference => reference.permalink === id
            )
        )
        .map((id, index) => ({
          id: generateUUID(),
          permalink: id,
          title: null,
          position: filteredReferences.length + index,
          __typename: ProductPositionInstallationReferenceTypename
        })) || []

    onSubmit([...filteredReferences, ...referencesToBeCreated])
    closeOverlay()
  })

  const installationReferenceSelectionModel =
    useInstallationReferenceSelectionModel({
      onSubmit: handleSubmit,
      defaultFilters,
      defaultSelectedInstallationReferenceIDs
    })

  return { closeOverlay, installationReferenceSelectionModel }
}
